import { Data, List, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import FileSpecConfigurationView from '../../FileSpecConfigurations/Views/FileSpecConfigurationView';
import ManageExportColumnSpecComponentVM from '../Components/ManageExportColumnSpecComponentVM';
import CreateExportColumnSpecCommand from '../Models/Commands/CreateExportColumnSpecCommand';
import UpdateExportColumnSpecCommand from '../Models/Commands/UpdateExportColumnSpecCommand';
import StaticExportColumnSpecSearchCriteria from '../Models/Queries/StaticExportColumnSpecSearchCriteria';
import StaticExportColumnSpecLookup from '../Models/Lookups/StaticExportColumnSpecLookup';
import StaticExportFileSpecLookup from '../../FileSpecConfigurations/Models/Lookups/StaticExportFileSpecLookup';

@NeoModel
export default class ExportColumnConfigurationsVM extends Views.ViewModelBase {

    constructor(
        public exportColumnSpecApiClient = AppService.get(Types.ApiClients.StaticExportColumnSpecApiClient),
        public staticExportFileSpecApiClient = AppService.get(Types.ApiClients.StaticExportFileSpecApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private navigation = AppService.get(Types.Neo.Routing.NavigationHelper),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public exportColumnConfigurationSearchCriteria = new StaticExportColumnSpecSearchCriteria();
    public fileSpecDropdownList = new List(StaticExportFileSpecLookup);
    public manageExportColumnSpecComponentVM = new ManageExportColumnSpecComponentVM();
    public exportFileSpecId: number = 0;
    public showManageColumnComponent: boolean = false;

    public pageManager = new Data.PageManager(this.exportColumnConfigurationSearchCriteria, StaticExportColumnSpecLookup,
        this.exportColumnSpecApiClient.getExportColumnSpecsPagedListAsync, {
            pageSize: 10,
            sortAscending: true,
            sortBy: "columnNumber",
            initialTaskRunner: this.taskRunner,
        });

    public async initialise(fileSpecId?: number, fileSpecProps?: string) {
        this.exportFileSpecId = Number(fileSpecId);
        this.exportColumnConfigurationSearchCriteria.staticExportFileSpecId = this.exportFileSpecId;
        this.exportColumnConfigurationSearchCriteria.fileTypeConcatenated = fileSpecProps!;

        await this.loadFileSpecs();
        reaction(() => this.exportColumnConfigurationSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), {fireImmediately: false, delay: 650 });
    }

    public addColumn() {
        this.showManageColumnComponent = true;
        this.manageExportColumnSpecComponentVM.initialise(this.exportColumnConfigurationSearchCriteria.staticExportFileSpecId!, this.exportColumnConfigurationSearchCriteria.fileTypeConcatenated!);
    }

    public onBack() {
        this.navigation.navigateToView(FileSpecConfigurationView);
    }

    public async cancel() {
        this.manageExportColumnSpecComponentVM.managedExportColumnSpec = new StaticExportColumnSpecLookup();
        this.showManageColumnComponent = false;
    }

    public async clearSearch() {
        this.exportColumnConfigurationSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editColumnSpec(columnSpecLookup: StaticExportColumnSpecLookup) {
        this.manageExportColumnSpecComponentVM.managedExportColumnSpec.mapFrom(columnSpecLookup).toJSObject();
        this.showManageColumnComponent = true;
        this.manageExportColumnSpecComponentVM.initialise(Number(this.manageExportColumnSpecComponentVM.managedExportColumnSpec.columnNumber));
    }

    public async loadFileSpecs() {
        const response = await this.taskRunner.waitFor(this.staticExportFileSpecApiClient.getExportFileSpecDropdownList(""));
        this.fileSpecDropdownList.set(response.data);
    }

    public async saveExportColumnSpec() {
        this.taskRunner.run(async () => {
            var response = this.manageExportColumnSpecComponentVM.managedExportColumnSpec.staticExportColumnSpecId > 0
            ? await this.exportColumnSpecApiClient.updateExportColumnSpec((this.mapColumnSpecToUpdateCommand(this.manageExportColumnSpecComponentVM.managedExportColumnSpec)!.toJSObject()))
            : await this.exportColumnSpecApiClient.addNewExportColumnSpec((this.mapColumnSpecToCreateCommand(this.manageExportColumnSpecComponentVM.managedExportColumnSpec)!.toJSObject()));

            if (response.data.success) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.ColumnSaved, NotificationDuration.Standard);
                await this.pageManager.refreshData();
            }
            else {
                NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
            }
            this.manageExportColumnSpecComponentVM.managedExportColumnSpec = new StaticExportColumnSpecLookup();
            this.showManageColumnComponent = false;
            await this.loadFileSpecs();
        })
    }

    private mapColumnSpecToCreateCommand(columnSpecLookup: StaticExportColumnSpecLookup) {
        var command = new CreateExportColumnSpecCommand().mapFrom(columnSpecLookup);
        return command;
    }

    private mapColumnSpecToUpdateCommand(columnSpecLookup: StaticExportColumnSpecLookup) {
        var command = new UpdateExportColumnSpecCommand().mapFrom(columnSpecLookup);
        return command;
    }
}